import { useMutation, useQueryClient } from "@tanstack/react-query";
import * as Sentry from "@sentry/react";
import { supabase } from "@/integrations/supabase/client";
import { useIsSeedViewer } from "@/hooks/useIsSeedViewer";

// Item 14 — Fit-score writer.
//
// `compute_fit_score(p_org_id)` derives the agent from `auth.uid()` server-side
// and upserts a 0-100 score into `agent_org_fit_scores` (ON CONFLICT
// (agent_user_id, org_id) DO UPDATE). It early-returns NULL without writing
// when the agent's weighted preferences total zero, so a sparse-preference
// agent is a cheap server-side no-op — no client-side completeness gate needed.
//
// This hook fans the RPC across every published org so a beta agent who never
// opens an individual org profile still gets scored. Fire-and-forget: a
// per-org failure is captured to Sentry and the loop continues. No toasts,
// no onSuccess/onError, no user-visible UI.
export function useRecomputeFitScores() {
  const isSeedViewer = useIsSeedViewer();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (): Promise<void> => {
      // Real agents only fan-call against non-seed published orgs (the orgs
      // they can actually see on /explore); the demo agent retains the
      // testing flow against seed orgs.
      const orgsQuery = supabase
        .from("organizations")
        .select("id")
        .eq("is_published", true)
        .eq("is_seed", isSeedViewer);
      const { data: orgs, error } = await orgsQuery;

      if (error) {
        Sentry.captureException(error, {
          tags: { component: "fit-score-recompute", stage: "list-orgs" },
        });
        return;
      }

      if (!orgs || orgs.length === 0) return;

      const results = await Promise.allSettled(
        orgs.map((o) => supabase.rpc("compute_fit_score", { p_org_id: o.id })),
      );

      results.forEach((result, i) => {
        const orgId = orgs[i].id;
        if (result.status === "rejected") {
          console.warn(
            "[fit-score-recompute] RPC threw for org",
            orgId,
            result.reason,
          );
          Sentry.captureException(result.reason, {
            tags: { component: "fit-score-recompute", org_id: orgId },
          });
        } else if (result.value.error) {
          console.warn(
            "[fit-score-recompute] RPC errored for org",
            orgId,
            result.value.error,
          );
          Sentry.captureException(result.value.error, {
            tags: { component: "fit-score-recompute", org_id: orgId },
          });
        }
      });

      // Refresh react-query reads of the freshly-written scores.
      // ["fit-score"] backs OrganizationProfile's ["fit-score", orgId, userId]
      // query (the org-detail page). ["agent-fit-scores"] backs ExplorePage's
      // ["agent-fit-scores", userId] query, so /explore badges (grid, map,
      // list, Quick View) refresh reactively after the fan-call writes scores,
      // with no re-mount or navigation needed.
      queryClient.invalidateQueries({ queryKey: ["fit-score"] });
      queryClient.invalidateQueries({ queryKey: ["agent-fit-scores"] });
    },
  });
}
